import { UAParser } from "ua-parser-js";
import { supabase } from "../integrations/supabase/client";

export type DeviceSessionSnapshot = {
  fingerprint: string;
  title: string;
  location: string;
  timezone: string;
  ip_address?: string;
  isp_name?: string;
  browser_version?: string;
  os_version?: string;
  device_type?: string;
};

const ENABLE_IP_GEOLOOKUP = import.meta.env.VITE_ENABLE_IP_GEOLOOKUP === "true";

let _cachedGeoResult: { location: string | null; ip: string | null; isp: string | null } | null = null;
let _geoBackoffUntil = 0;

export async function getDetailedLocation(): Promise<{ location: string | null; ip: string | null; isp: string | null }> {
  const empty = { location: null, ip: null, isp: null };

  if (!ENABLE_IP_GEOLOOKUP) return empty;
  if (_cachedGeoResult) return _cachedGeoResult;
  if (Date.now() < _geoBackoffUntil) return empty;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
    // Use Supabase Edge Function proxy to avoid CORS/429 issues with ipapi.co
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return empty;

    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ip-lookup`, {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    if (res.status === 429) {
      // Back off for 30 minutes on rate limit
      _geoBackoffUntil = Date.now() + 30 * 60 * 1000;
      return empty;
    }
    if (!res.ok) return empty;
    const data = await res.json();
    _cachedGeoResult = {
      location: data.city && data.country_name ? `${data.city}, ${data.country_name}` : null,
      ip: data.ip || null,
      isp: data.org || null
    };
    return _cachedGeoResult;
  } catch {
    // Back off for 10 minutes on network/CORS errors to prevent spamming
    _geoBackoffUntil = Date.now() + 10 * 60 * 1000;
    return empty;
  }
}

export async function getDeviceSessionSnapshot(): Promise<DeviceSessionSnapshot> {
  const parser = new UAParser(navigator.userAgent);
  const browser = parser.getBrowser();
  const os = parser.getOS();
  const device = parser.getDevice();

  const browserName = browser.name || "Browser";
  const osName = os.name || "OS";
  const deviceType = device.type || "desktop";

  // Meta-style title
  let deviceTitle = "";
  if (device.model) {
    deviceTitle = `${device.vendor ? device.vendor + " " : ""}${device.model} (${browserName})`;
  } else if (deviceType === "mobile" || deviceType === "tablet") {
    deviceTitle = `${osName} ${deviceType} (${browserName})`;
  } else {
    deviceTitle = `${osName} PC (${browserName})`;
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Unknown/Timezone";
  const locationProxy = timezone.includes("/")
    ? timezone.split("/").slice(-1)[0].replace(/_/g, " ")
    : timezone;

  const { location, ip, isp } = await getDetailedLocation();

  // Fingerprint for uniqueness
  const fingerprint = [
    browserName,
    browser.version,
    osName,
    os.version,
    device.vendor,
    device.model,
    deviceType,
    navigator.language || "unknown-lang",
    timezone,
  ].filter(Boolean).join("|");

  return {
    fingerprint,
    title: deviceTitle,
    location: location || locationProxy,
    timezone,
    ip_address: ip || undefined,
    isp_name: isp || undefined,
    browser_version: browser.version || undefined,
    os_version: os.version || undefined,
    device_type: deviceType,
  };
}
